/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


$(document).ready(function() {

  if (window.location.pathname.includes('search') === true) {
    console.log('You are doing great')
    document.getElementsByClassName('filters')[0].style.display = 'none';
    document.getElementsByClassName('utility-bar__left')[0].style.visibility = 'hidden';
  }
  
  /*
    The following code handles the dynamic closing of filter sections on collection
    pages when a new filter is opened. Without the code below, all of the filter 
    options could be open similtaneously which would be a poor user experience.

    Irish Titan - 02/11/2025
  */
  
  $(document).on("click", 'toggle-target', function() {
    const toggleTargets = $("#CollectionFilterForm").find('toggle-target');
    const filterGroupItems = $("#CollectionFilterForm").find('.filter-group__items');

    for (let i=0; i<toggleTargets.length; i++) {
      if ($(this)[0].textContent.trim() !== $(toggleTargets[i])[0].textContent.trim()) {
        $(toggleTargets[i]).addClass('toggle-target-toggler--is-hidden');
        $(filterGroupItems[i]).addClass('toggle-target--hidden');
      }
    }
  });


  /*
    The following code handles moving the date selector up and down dynamically
    depending on whether or not the applied filters section is visible on the page.
    Without the mutation observer below, the date picker would actually appear
    above the applied filters section which looks bad.

    Irish Titan - 02/14/2025
  */
  const filterListIsVisibleOnPageLoad = document.getElementsByClassName("filter-group--applied").length > 0;
  
  const moveDateFilterSelectorDown = (dateFilterSelector) => {
    dateFilterSelector.classList.add('applied-filters-section-visible')
  }
  
  const moveDateFilterSelectorUp = (dateFilterSelector) => {
    dateFilterSelector.classList.remove('applied-filters-section-visible')
  }

  // Checks if the filter section is visible on page load and if it is,
  // we can move it down.
  if (filterListIsVisibleOnPageLoad) {
    
    // We need to pass the HTML element here because this instance of the
    // date selector gets removed when filters get added and removed. Ideally,
    // we would be able to have one variable for the element, but that 
    // unfortunately won't work.
    moveDateFilterSelectorDown(document.getElementById("CollectionFilterForm").getElementsByClassName("filter-group")[0])
  }

  if(document.getElementsByClassName("filters__inner").length > 0) {
    const targetNode = document.getElementsByClassName("filters__inner")[0];
    const config = { attributes: false, childList: true, subtree: false };
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          const dateFilterSelector = document.getElementById("CollectionFilterForm").getElementsByClassName("filter-group")[0];
  
          // If the filters list gets added to the page, we need to move the date 
          // selector down.
          if (mutation.addedNodes.length >= mutation.removedNodes.length) {
            for (ele of mutation.addedNodes) {
              if (ele.classList !== undefined && ele.classList.contains("filter-group--applied")) {
                moveDateFilterSelectorDown(dateFilterSelector)
                break
              }
            }
          } else if (dateFilterSelector.classList.contains('filters-applied')) {
          // Otherwise we need to move the date selector back up.
            moveDateFilterSelectorUp(dateFilterSelector)
          }
        }
      }
    };
    
    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);
  }
  

  $("#customProductOptionsButton").on("click", function() {
    $(".pdp-hide").removeClass("pdp-hide");
    $("#customProductOptionsButton").addClass("pdp-hide");
    $("#workshopSelectedButton").removeClass("pdp-hide");
  });


  /*
    The following code hides the "Upcoming Workshops" section on instructor pages
    if they do not have any upcoming workshops.

    Irish Titan - 03/20/2025
  */
  const featuredCollectionLength = $(".section-featured-collection").find(".product-block").length;
  const featuredCollectionHeader = $('.section-rich-text:contains("Scheduled Workshops")');
  
  if (featuredCollectionLength === 0) {
    featuredCollectionHeader.css("display", "none");
  }


  /*
    The following code hides the the email and/or website logo on instructor
    pages if the instructor does not have a registered email and/or website.

    Irish Titan - 03/24/2025
  */
  const logoListLinks = $(".logo-list-container").find(".logo-list__logo");
  
  for (const link of logoListLinks) {
    if (link.textContent.trim() === "" || link.textContent.trim() === null) {
      $(link).css("display", "none");
    }
  }

  if (window.location.pathname.includes("products")) {
    const productInfoBlockParagraphs = $(".product-info-block").find("p");
    let instructorParagraph;

    console.log(productInfoBlockParagraphs)
    for (p of productInfoBlockParagraphs) {
      console.log(p)
      if (p?.innerText !== "" && p.innerText.toLowerCase().includes("instructor")) {
        instructorParagraph = p;
      }
    }
    const innerText = instructorParagraph.innerText;
    const instructorName = innerText.replace("Instructor:", "").trim();
    const urlSlug = instructorName.replaceAll(" ", "-").toLowerCase()
    const anchorTag = `<p>Instructor: <a href="/pages/${urlSlug}">${instructorName}</a></p>`

    console.log("anchorTag", anchorTag)
    $(instructorParagraph).replaceWith(anchorTag)
    
  }
  
});



